import React, { useState, useEffect } from 'react'
import Modal from 'aldoo-ra/Modal' // Replace with actual Modal import
import ToggleSwitch from 'aldoo-ra/ToggleSwitch' // Replace with actual ToggleSwitch import
import Button from 'aldoo-ra/Button' // Replace with actual Button import
import Localization from 'aldoo-ra/Localization' // Assuming you have this import for getting languages
import { showPromptDialog } from 'aldoo-ra/PromptDialog'
import LanguageAutoTranslateDialog from './language-auto-translate-dialog' // Import the AutoTranslateDialog component

const LanguageDialog = ({ language, isOpen, onClose, controller }) => {
  const {
    languages: availableLanguages,
    refreshLanguages: refreshLocalizationLanguages,
  } = Localization()
  const [code, setLanguageCode] = useState('')
  const [name, setLanguageName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [primary, setPrimary] = useState(false)
  const [release, setRelease] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [autoTranslateDialogOpen, setAutoTranslateDialogOpen] = useState(false)

  useEffect(() => {
    if (isOpen) {
      refreshLocalizationLanguages() // Fetch the available languages when the dialog opens
      clearForm()
      //set values from language object
      setLanguageCode(language.code)
      setLanguageName(language.name)
      setDisplayName(language.displayName)
      setPrimary(language.primary === true)
      setRelease(language.release === true)
    }
  }, [isOpen])

  const clearForm = () => {
    setLanguageCode('')
    setLanguageName('')
    setDisplayName('')
    setPrimary(false)
    setRelease(false)
    setErrorMessage('')
  }

  const validateAndSave = async () => {
    // Validate that none of the fields are empty
    if (!code || !name || !displayName) {
      setErrorMessage('Please fill out all fields.')
      return
    }

    // Check for duplicates in existing languages
    const isDuplicate = availableLanguages
      // Filter out the current language from the list
      .filter((lang) => lang.id !== language?.id)
      .some(
        (lang) =>
          lang.code === code ||
          lang.name === name ||
          lang.displayName === displayName,
      )

    if (isDuplicate) {
      setErrorMessage(
        'A language with the same code, name, or display name already exists.',
      )
      return
    }

    // Clear error message and save the data using controller.saveLanguage
    setErrorMessage('')

    try {
      await controller.saveLanguage({
        id: language?.id,
        code,
        name,
        displayName,
        primary: primary === true,
        release: release === true,
      })
      onClose() // Close the modal after save
    } catch (error) {
      setErrorMessage('Failed to save the language. Please try again.')
    }
  }

  // Clear error message on input change
  const handleInputChange = (setter) => (event) => {
    setter(event.target.value)
    if (errorMessage) setErrorMessage('')
  }

  const handleDelete = async () => {
    showPromptDialog({
      title: 'Delete Language',
      message:
        'Are you sure you want to delete this language? This will delete all translations for it.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await controller.deleteLanguage(language.code)
          onClose()
        } catch (error) {
          setErrorMessage('Failed to delete the language. Please try again.')
        }
      },
    })
  }

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      className="relative w-full max-w-[500px] h-full md:h-auto"
      title={language?.name}
    >
      <div className="p-4 text-admin_text">
        <div className="grid grid-cols-12 gap-3 mb-5">
          <div className="col-span-4 mb-4">
            <label className="block font-semibold">
              Language Code
            </label>
            
            <input
              type="text"
              value={code}
              onChange={handleInputChange(setLanguageCode)}
              className="w-full  text-admin_text rounded bg-admin_canvas border border-admin_bordered focus:outline-none p-2"
              placeholder="code"
            />
          </div>

          <div className="col-span-4 mb-4">
            <label className="block font-semibold">
              Language Name
            </label>

            <input
              type="text"
              value={name}
              onChange={handleInputChange(setLanguageName)}
              className="w-full  text-admin_text rounded bg-admin_canvas border border-admin_bordered focus:outline-none p-2"
              placeholder="name"
            />
          </div>

          <div className="col-span-4 mb-4">
            <label className="block font-semibold">
              Display Name
            </label>

            <input
              type="text"
              value={displayName}
              onChange={handleInputChange(setDisplayName)}
              className="w-full  text-admin_text rounded bg-admin_canvas border border-admin_bordered focus:outline-none p-2"
              placeholder="display name"
            />
          </div>
        </div>

        {/* add number of words from language.words */}
        <div className="mb-4 flex flex-row items-center">
          <label className=" block font-semibold">
            Number of Words:
          </label>

          <span type="text" className="text-admin_text p-2">
            {language?.words}
          </span>
        </div>

        {/* ToggleSwitch for Primary */}
        <div className="mb-4 flex items-center">
          <ToggleSwitch 
            variant="admin"
            isOn={primary} 
            onChange={setPrimary} 
          />

          <span className="font-semibold ml-4">
            Primary
          </span>

          <p className="max-w-[260px] text-sm text-admin_text_secondary ml-5">
            Set this language as Primary to use it for auto-translation.
          </p>
        </div>

        {/* ToggleSwitch for Live */}
        <div className="mb-4 flex items-center">
          <ToggleSwitch 
            variant="admin"
            isOn={release} 
            onChange={setRelease} 
          />

          <span className="font-semibold ml-2">
            Live
          </span>

          <p className="max-w-[260px] text-admin_text_secondary text-sm ml-5">
            Live languages are available to end users.
          </p>
        </div>

        <div className="mb-4 flex items-center">
          <Button
            variant="admin_primary"
            className="w-1/2"
            onClick={() => setAutoTranslateDialogOpen(true)}
          >
            Auto Translate
          </Button>

          <p className="text-sm text-admin_text_secondary ml-5">
            Run auto translate on all language keys.
          </p>
        </div>

        {/* Save Button */}
        <div className="flex justify-between items-center">
          <Button
            variant="admin_secondary"
            className="w-full sm:w-32"
            onClick={handleDelete}
          >
            Delete
          </Button>
          
          {/* Error Message */}
          <p className="h-[2em] flex-1 text-sm text-admin_error mb-4">
            {errorMessage}
          </p>
          
          <Button
            variant="admin_primary"
            className="w-full sm:w-32"
            onClick={validateAndSave}
          >
            Save
          </Button>
        </div>
      </div>

      <LanguageAutoTranslateDialog
        isOpen={autoTranslateDialogOpen}
        onClose={() => setAutoTranslateDialogOpen(false)}
        language={language}
        controller={controller}
      />
    </Modal>
  )
}

export default LanguageDialog
